import type { GlossaryFn } from "src/types/glossary";

interface SchoolSelectionLimitTextOptions {
  /**
   * Whether or not the maximum number of schools have been selected.
   */
  limitReached: boolean;

  /**
   * The optional, maximum number of schools that can be selected.
   */
  maxSchools?: number;

  /**
   * The optional, minimum number of schools that must be selected.
   */
  minSchools?: number;
}

/**
 * A function which returns assistive text to display with school selection.
 * Return the assistive copy to display with school selection.
 */
export function schoolSelectionLimitText(
  glossary: GlossaryFn,
  options: SchoolSelectionLimitTextOptions
): string | null {
  const { limitReached, maxSchools = 0, minSchools = 1 } = options;

  if (limitReached) {
    return glossary`School limit reached`;
  }

  if (minSchools > 1) {
    if (maxSchools > minSchools) {
      return glossary`Add between ${minSchools} to ${maxSchools} schools`;
    }

    if (maxSchools === minSchools) {
      return glossary`Add ${maxSchools} schools`;
    }

    // Ignore a maximum below the minimum.
    return glossary`Add at least ${minSchools} schools`;
  }

  if (maxSchools > 1) {
    return glossary`Add up to ${maxSchools} schools`;
  }

  if (maxSchools > 0) {
    return glossary`Add up to ${maxSchools} school`;
  }

  return null;
}

interface AddSchoolButtonStateOptions {
  /**
   * Whether or not the maximum number of schools have been selected.
   */
  limitReached: boolean;

  /**
   * Whether any eligible schools are left to add.
   */
  hasSchoolsToAdd: boolean;

  /**
   * Whether any schools have been selected.
   */
  hasSelectedSchools: boolean;
}

/**
 * Whether the "Add school" button is disabled, and the reason shown for it.
 */
export function addSchoolButtonState(
  glossary: GlossaryFn,
  options: AddSchoolButtonStateOptions
): { disabled: boolean; disabledReason?: string } {
  const { limitReached, hasSchoolsToAdd, hasSelectedSchools } = options;

  if (limitReached) {
    return {
      disabled: true,
      disabledReason: glossary`School limit reached. Remove a school to add another.`,
    };
  }

  if (!hasSchoolsToAdd) {
    return {
      disabled: true,
      disabledReason: hasSelectedSchools
        ? glossary`No more schools are available`
        : glossary`No schools are available`,
    };
  }

  return { disabled: false };
}
